<script>
import { db } from "../../firebase";

// Cache simple para evitar repetir consultas por cada re-render de la tabla.
// Key: numero de caso. Value: { ts: number, data: Array<object> }
const NOTIF_CACHE_TTL_MS = 60 * 1000;
const notificacionesCache = new Map();

export default {
  name: "CasoTimeline",
  props: {
    caso: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      timelinePoints: [
        {
          label: "Asignación de caso",
          time: null,
          momento: "Asignación de caso",
          fechahora: null,
        },
        {
          label: "Estimación de llegada",
          time: null,
          momento: "Estimación de llegada",
          fechahora: null,
          esCalculado: true, // Marca que este punto es calculado, no viene de Firebase
        },
        {
          label: "Llegada a sitio",
          time: null,
          momento: "Llegada al sitio",
          fechahora: null,
        },
        {
          label: "Toma de Fotografías",
          time: null,
          momento: "Carga de primera foto",
          fechahora: null,
        },
        {
          label: "Cierre de caso",
          time: null,
          momento: "Cierre de caso",
          fechahora: null,
        },
      ],
      loading: true,
      ultimaNotificacion: null,
    };
  },
  computed: {
    currentStep() {
      // Si no hay ninguna notificación, devolvemos -1 para que todo sea gris
      if (!this.ultimaNotificacion || !this.ultimaNotificacion.momento) {
        return -1;
      }

      // Obtener el momento de la última notificación
      const momentoUltima = this.ultimaNotificacion.momento;
      const momentoUltimaNormalizado = this.normalizarMomento(momentoUltima);

      // Usar la función helper para encontrar el punto
      let indicePunto = this.encontrarPuntoTimeline(momentoUltimaNormalizado);

      if (indicePunto === -1) {
        // Si no se encuentra el momento, devolvemos -1 (todo gris)
        // Esto puede pasar si el momento en Firebase no coincide con ninguno esperado
        console.warn(
          "Momento no encontrado en timeline:",
          momentoUltima,
          "Normalizado:",
          momentoUltimaNormalizado
        );
        return -1;
      }

      // Ajustar el índice si el punto encontrado es "Llegada al sitio" (índice 2 después de agregar estimación)
      // porque el punto de estimación está en el índice 1
      // Si la última notificación es "Llegada al sitio", el punto de estimación también debe estar completado
      if (indicePunto === 2) {
        // Llegada al sitio - el punto de estimación (índice 1) también está completado
        // pero currentStep debe ser 2 para que llegada sea naranja
        return 2;
      }

      // Si es "Cierre de caso", devolvemos un valor mayor para que todo sea verde
      if (indicePunto === this.timelinePoints.length - 1) {
        return this.timelinePoints.length; // Para que todos sean verdes
      }

      // Para otros momentos, devolvemos el índice para que ese punto sea naranja
      return indicePunto;
    },
  },
  async mounted() {
    await this.loadNotificaciones();
  },
  watch: {
    caso: {
      handler() {
        this.loadNotificaciones();
      },
      immediate: false,
    },
  },
  methods: {
    // Función para normalizar momentos (maneja variaciones y caracteres especiales)
    normalizarMomento(momento) {
      if (!momento) return "";
      return momento
        .toLowerCase()
        .trim()
        .replace(/[\u200B-\u200D\uFEFF]/g, "") // Eliminar caracteres invisibles/zero-width
        .replace(/\s+/g, " ") // Normalizar múltiples espacios a uno solo
        .replace(/llegada\s+(a|al)\s+sitio/i, "llegada al sitio")
        .replace(/toma\s+de\s+fotograf[íi]as/i, "carga de primera foto")
        .replace(
          /^(cerrado|cierre|cierre del caso|cierre de caso|cancelado|conciliado|no conciliado)$/i,
          "cierre de caso"
        );
    },
    // Función para encontrar el punto de timeline que coincide con un momento normalizado
    encontrarPuntoTimeline(momentoNormalizado) {
      // Primero intentar comparación exacta
      for (let i = 0; i < this.timelinePoints.length; i++) {
        // Saltar puntos calculados (no vienen de Firebase)
        if (this.timelinePoints[i].esCalculado) continue;

        const puntoNormalizado = this.normalizarMomento(
          this.timelinePoints[i].momento
        );
        if (puntoNormalizado === momentoNormalizado) {
          return i;
        }
      }
      // Si no coincide exactamente, intentar comparación parcial
      for (let i = 0; i < this.timelinePoints.length; i++) {
        // Saltar puntos calculados (no vienen de Firebase)
        if (this.timelinePoints[i].esCalculado) continue;

        const puntoNormalizado = this.normalizarMomento(
          this.timelinePoints[i].momento
        );
        if (
          (momentoNormalizado.includes("asignación") &&
            puntoNormalizado.includes("asignación")) ||
          (momentoNormalizado.includes("llegada") &&
            puntoNormalizado.includes("llegada")) ||
          (momentoNormalizado.includes("carga") &&
            puntoNormalizado.includes("carga")) ||
          (momentoNormalizado.includes("foto") &&
            puntoNormalizado.includes("foto")) ||
          (momentoNormalizado.includes("cierre") &&
            puntoNormalizado.includes("cierre"))
        ) {
          return i;
        }
      }
      return -1;
    },
    // Función para calcular el tiempo estimado entre asignación y llegada
    calcularTiempoEstimado(asignacionFecha, llegadaFecha) {
      if (!asignacionFecha || !llegadaFecha) {
        return null;
      }

      try {
        // Convertir las fechas del formato "2025-10-15 09:07:28" a objetos Date
        const fechaAsignacion = new Date(
          asignacionFecha.replace(
            /(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/,
            "$1T$2"
          )
        );
        const fechaLlegada = new Date(
          llegadaFecha.replace(
            /(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/,
            "$1T$2"
          )
        );

        // Calcular la diferencia en milisegundos
        const diferencia = fechaLlegada - fechaAsignacion;

        if (diferencia < 0) {
          return null; // Si la fecha de llegada es anterior a asignación, no calcular
        }

        // Convertir a horas, minutos y segundos
        const horas = Math.floor(diferencia / (1000 * 60 * 60));
        const minutos = Math.floor(
          (diferencia % (1000 * 60 * 60)) / (1000 * 60)
        );
        const segundos = Math.floor((diferencia % (1000 * 60)) / 1000);

        // Formatear como "Xh Ym Zs" o solo los componentes necesarios
        let tiempoFormateado = "";
        if (horas > 0) {
          tiempoFormateado += `${horas}h `;
        }
        if (minutos > 0 || horas > 0) {
          tiempoFormateado += `${minutos}m `;
        }
        tiempoFormateado += `${segundos}s`;

        return tiempoFormateado.trim();
      } catch (error) {
        console.error("Error al calcular tiempo estimado:", error);
        return null;
      }
    },
    ordenarNotificacionesPorFechaDesc(notificaciones = []) {
      return [...notificaciones].sort((a, b) => {
        const fechaA = a?.fechahora || "";
        const fechaB = b?.fechahora || "";
        if (fechaA === fechaB) return 0;
        if (!fechaA) return 1;
        if (!fechaB) return -1;
        return fechaA < fechaB ? 1 : -1;
      });
    },
    construirCandidatosCaso(valor) {
      const candidatos = [];
      const agregar = (v) => {
        if (
          v === undefined ||
          v === null ||
          (typeof v === "number" && Number.isNaN(v))
        ) {
          return;
        }
        if (!candidatos.some((c) => c === v)) {
          candidatos.push(v);
        }
      };

      agregar(valor);

      const texto = String(valor || "").trim();
      if (texto) {
        agregar(texto);
        const numero = Number(texto);
        if (!Number.isNaN(numero)) agregar(numero);
      }

      if (typeof valor === "number" && !Number.isNaN(valor)) {
        agregar(String(valor));
      }

      return candidatos;
    },
    async loadNotificaciones() {
      if (!this.caso) {
        this.loading = false;
        this.ultimaNotificacion = null;
        return;
      }

      try {
        this.loading = true;
        const cacheKey = String(this.caso);
        const cached = notificacionesCache.get(cacheKey);
        let notificaciones = null;

        if (cached && Date.now() - cached.ts < NOTIF_CACHE_TTL_MS) {
          notificaciones = cached.data;
        } else {
          // Compatibilidad: en BD hay registros donde `caso` puede estar como número o string.
          // Si no se encuentra por el tipo original, se intenta con el alterno.
          const obtenerNotificacionesPorCaso = async (valorCaso) => {
            try {
              const snapshot = await db
                .collection("notificaciones")
                .where("caso", "==", valorCaso)
                .orderBy("fechahora", "desc")
                .limit(60)
                .get();
              return snapshot.docs.map((d) => d.data() || {});
            } catch (error) {
              // Fallback robusto: evita depender de índice compuesto y conserva docs sin fechahora.
              const snapshot = await db
                .collection("notificaciones")
                .where("caso", "==", valorCaso)
                .get();
              const docs = snapshot.docs.map((d) => d.data() || {});
              return this.ordenarNotificacionesPorFechaDesc(docs).slice(0, 60);
            }
          };

          const candidatosCaso = this.construirCandidatosCaso(this.caso);
          notificaciones = [];

          for (const candidato of candidatosCaso) {
            const resultado = await obtenerNotificacionesPorCaso(candidato);
            if (resultado.length) {
              notificaciones = resultado;
              break;
            }
          }

          notificacionesCache.set(cacheKey, {
            ts: Date.now(),
            data: notificaciones,
          });
        }

        if (!notificaciones || notificaciones.length === 0) {
          this.ultimaNotificacion = null;
          // Limpiar todos los tiempos
          this.timelinePoints = this.timelinePoints.map((point) => ({
            ...point,
            time: null,
            fechahora: null,
          }));
          return;
        }

        // Encontrar la última notificación (ordenar por fechahora)
        let ultimaNotif = null;
        let ultimaFecha = null;
        let primeraNotifSinFecha = null; // Guardar la primera notificación sin fecha como respaldo

        // Crear un mapa de índices de timeline a datos de notificaciones
        const notificacionesPorPunto = {};

        notificaciones.forEach((data) => {
          const momento = data.momento;
          const fechahora = data.fechahora;

          if (fechahora) {
            // Comparar fechas para encontrar la más reciente
            // Formato: "2025-10-15 09:07:28" - usar comparación de strings (formato ISO)
            if (!ultimaFecha || fechahora > ultimaFecha) {
              ultimaFecha = fechahora;
              ultimaNotif = {
                momento: momento,
                fechahora: fechahora,
              };
            }

            // Encontrar a qué punto de la timeline corresponde esta notificación
            const momentoNormalizado = this.normalizarMomento(momento);
            const indicePunto = this.encontrarPuntoTimeline(momentoNormalizado);

            if (indicePunto !== -1) {
              // Guardar la hora y fecha completa para este punto
              const hora = fechahora.split(" ")[1] || fechahora;

              // Si ya existe una notificación para este punto, mantener la más reciente
              if (
                !notificacionesPorPunto[indicePunto] ||
                fechahora > notificacionesPorPunto[indicePunto].fechahora
              ) {
                notificacionesPorPunto[indicePunto] = {
                  hora,
                  fechahora,
                };
              }
            }
          } else if (momento && !primeraNotifSinFecha) {
            // Guardar la primera notificación sin fecha como respaldo
            primeraNotifSinFecha = {
              momento: momento,
              fechahora: null,
            };
          }
        });

        // Si no se encontró ninguna notificación con fecha, usar la primera sin fecha
        if (!ultimaNotif && primeraNotifSinFecha) {
          ultimaNotif = primeraNotifSinFecha;
        }

        // Guardar la última notificación
        this.ultimaNotificacion = ultimaNotif;

        // Mapear las notificaciones a los puntos de la timeline
        this.timelinePoints = this.timelinePoints.map((point, index) => {
          // Si es el punto de estimación, calcular el tiempo basado en asignación y llegada
          if (point.esCalculado) {
            // Buscar los índices de asignación (0) y llegada (2, ya que estimación está en índice 1)
            const asignacionIndex = 0;
            const llegadaIndex = 2;

            const asignacionDatos = notificacionesPorPunto[asignacionIndex];
            const llegadaDatos = notificacionesPorPunto[llegadaIndex];

            if (
              asignacionDatos &&
              llegadaDatos &&
              asignacionDatos.fechahora &&
              llegadaDatos.fechahora
            ) {
              const tiempoEstimado = this.calcularTiempoEstimado(
                asignacionDatos.fechahora,
                llegadaDatos.fechahora
              );
              return {
                ...point,
                time: tiempoEstimado,
                fechahora: tiempoEstimado
                  ? `Tiempo estimado: ${tiempoEstimado}`
                  : null,
              };
            }
            return {
              ...point,
              time: null,
              fechahora: null,
            };
          }

          const datos = notificacionesPorPunto[index] || null;
          const tiempo = datos ? datos.hora : null;
          const fechaCompleta = datos ? datos.fechahora : null;
          return {
            ...point,
            time: tiempo,
            fechahora: fechaCompleta,
          };
        });
      } catch (error) {
        console.error("Error al cargar notificaciones:", error);
        this.ultimaNotificacion = null;
      } finally {
        this.loading = false;
      }
    },
    formatTime(time) {
      // Si el tiempo viene en formato completo, extraer solo la hora
      if (time && time.includes(" ")) {
        return time.split(" ")[1];
      }
      return time || "--:--:--";
    },
    formatDateTime(fechahora) {
      return fechahora || "Sin fecha";
    },
  },
};
</script>

<template>
  <div class="timeline">
    <div v-if="loading" class="loading">Cargando...</div>
    <template v-else>
      <!-- Línea inicial -->
      <div :class="['line', currentStep >= 0 ? 'line-green' : 'line-gray']" />

      <div
        v-for="(point, index) in timelinePoints"
        :key="index"
        class="point-wrap"
      >
        <!-- Círculo con tooltip personalizado -->
        <div class="tooltip-wrapper">
          <button
            class="dot"
            :class="[
              currentStep === -1
                ? 'dot-gray'
                : point.esCalculado
                ? // Para punto calculado, mostrar verde si tiene tiempo estimado
                  point.time
                  ? 'dot-green'
                  : 'dot-gray'
                : index < currentStep
                ? 'dot-green'
                : index === currentStep
                ? 'dot-orange'
                : 'dot-blue',
            ]"
            :aria-label="`${point.label}: ${
              point.esCalculado
                ? point.time || 'Sin datos'
                : formatTime(point.time)
            }`"
          />
          <div class="tooltip-content">
            <div class="tooltip-title">{{ point.label }}</div>
            <div
              class="tooltip-date"
              :class="[
                currentStep === -1
                  ? 'tooltip-date-gray'
                  : point.esCalculado
                  ? // Para punto calculado
                    point.time
                    ? 'tooltip-date-green'
                    : 'tooltip-date-gray'
                  : point.fechahora
                  ? 'tooltip-date-green'
                  : 'tooltip-date-orange',
              ]"
            >
              {{
                currentStep === -1
                  ? "Sin notificaciones"
                  : point.esCalculado
                  ? point.time
                    ? `Tiempo estimado: ${point.time}`
                    : "Faltan datos de asignación o llegada"
                  : point.fechahora
                  ? formatDateTime(point.fechahora)
                  : "Pendiente"
              }}
            </div>
          </div>
        </div>

        <!-- Línea entre puntos -->
        <div
          :class="[
            'line',
            currentStep === -1
              ? 'line-gray'
              : point.esCalculado
              ? // Para línea después del punto calculado
                // Verde si llegada está completada (currentStep >= 2)
                currentStep >= 2
                ? 'line-green'
                : 'line-gray'
              : index < currentStep
              ? 'line-green'
              : 'line-gray',
          ]"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.timeline {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
}
/* Tooltip personalizado sin contenedores adicionales */
.tooltip-wrapper {
  position: relative;
  display: inline-block;
}
.tooltip-wrapper .tooltip-content {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  white-space: nowrap;
  margin-bottom: 8px;
}
.tooltip-wrapper .tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #000000;
}
.tooltip-wrapper:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}
.point-wrap {
  display: flex;
  align-items: center;
}
.line {
  height: 2px;
  width: 16px;
  border-radius: 999px;
  transition: background-color 0.2s ease;
}
.line-green {
  background-color: #22c55e;
}
.line-gray {
  background-color: #d1d5db;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
  transition: transform 0.2s ease, background-color 0.2s ease;
}
.dot:hover {
  transform: scale(1.25);
}
.dot-green {
  background-color: #22c55e;
}
.dot-orange {
  background-color: #f97316;
}
.dot-blue {
  background-color: #9ca3af;
}
.dot-gray {
  background-color: #9ca3af;
}
.tooltip-content {
  min-width: 140px;
  background-color: #000000;
  color: #ffffff;
  padding: 8px 10px;
  border-radius: 6px;
  border: none;
  box-shadow: none;
  margin: 0;
}
.tooltip-title {
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 13px;
}
.tooltip-date {
  font-size: 12px;
}
.tooltip-date-green {
  color: #22c55e;
}
.tooltip-date-orange {
  color: #fb923c;
}
.tooltip-date-gray {
  color: #9ca3af;
}
.loading {
  font-size: 12px;
  color: #6b7280;
  padding: 4px 0;
}
</style>
